interface IndividualAcknowledgementData {
  applicantName: string;
  email: string;
  nationalIdNumber: string;
  city: string;
}

interface IndividualAcknowledgementProps {
  lang: 'en' | 'ar';
  data: IndividualAcknowledgementData;
  /** Stable date used in the document header. Defaults to now. */
  documentDate?: Date;
}

/**
 * Canonical copy for the individual referral partner subscription.
 * Arabic and English MUST be exact translations of one another — same
 * sections, same number of bullets, same order, same emphasis, same
 * punctuation intent. Edit both sides together; never edit one alone.
 */
const COPY = {
  en: {
    title: "Referral Partner Subscription",
    disclaimer: "(This subscription is non-binding and is not a contract)",
    labels: {
      name: "Name",
      email: "Email",
      nationalId: "National ID",
      city: "City",
      date: "Date",
    },
    welcome: {
      title: "Welcome",
      body: "We are glad to welcome you as a Referral Partner on the Fusion Media platform. The purpose of this subscription is to introduce you to the nature of the partnership and what each party can expect.",
    },
    youGet: {
      title: "What You Get",
      items: [
        "Access to a dedicated dashboard to manage your clients.",
        "Full access to browse the inventory of advertising, influencers, and events.",
        "Refer clients and earn a commission on every completed deal.",
        "Commission calculated according to the commission tiers published on the platform (subject to change; the affiliate will be notified of any changes).",
      ],
    },
    expected: {
      title: "What Is Expected of You",
      items: [
        "Provide accurate information about yourself and your clients.",
        "Promote the ads in a manner that respects the regulations and the provisions of Islamic Sharia.",
        "Do not claim to be an employee or an official representative of the platform.",
      ],
    },
    commissions: {
      title: "Commissions",
      body: "Due commissions are paid within five (5) business days from the platform's receipt of the full deal value from the client, and into your registered bank account.",
    },
    ending: {
      title: "Ending the Partnership",
      body: "Either party has the right to end the relationship at any time and without the need to give reasons, while you retain your right to commissions due on deals completed before termination.",
    },
    notice: {
      label: "Notice",
      body: "This subscription is illustrative and does not create binding legal obligations on either party, and is not a contract. The platform's published policies remain the governing reference.",
    },
  },
  ar: {
    title: "اشتراك كشريك إحالة",
    disclaimer: "(هذا الاشتراك غير ملزم قانونياً ولا يُعد عقداً)",
    labels: {
      name: "الاسم",
      email: "البريد الإلكتروني",
      nationalId: "رقم الهوية",
      city: "المدينة",
      date: "التاريخ",
    },
    welcome: {
      title: "مرحباً بك",
      body: "نسعد بانضمامك كشريك إحالة في منصة فيوجن ميديا. الهدف من هذا الاشتراك هو تعريفك بطبيعة الشراكة وما يمكن أن يتوقعه كل طرف.",
    },
    youGet: {
      title: "ما تحصل عليه",
      items: [
        "الوصول إلى لوحة تحكم خاصة بك لإدارة عملائك.",
        "تصفح كامل لمخزون الإعلانات والمؤثرين والفعاليات.",
        "إحالة العملاء وكسب عمولة عن كل صفقة مكتملة.",
        "عمولة تُحتسب وفق فئات العمولة المعلنة في المنصة (قابلة للتغيير، وسيتم إشعار شريك الإحالة بأي تغييرات).",
      ],
    },
    expected: {
      title: "ما هو متوقع منك",
      items: [
        "تقديم معلومات صحيحة عنك وعن عملائك.",
        "الترويج للإعلانات بأسلوب يحترم الأنظمة وأحكام الشريعة الإسلامية.",
        "عدم الادعاء بصفة موظف أو ممثل رسمي للمنصة.",
      ],
    },
    commissions: {
      title: "العمولات",
      body: "تُصرف العمولات المستحقة خلال خمسة (5) أيام عمل من استلام المنصة لكامل قيمة الصفقة من العميل، وعبر حسابك البنكي المسجل.",
    },
    ending: {
      title: "إنهاء الشراكة",
      body: "يحق لأي من الطرفين إنهاء العلاقة في أي وقت ودون الحاجة لإبداء أسباب، مع احتفاظك بحقك في العمولات المستحقة عن الصفقات المكتملة قبل الإنهاء.",
    },
    notice: {
      label: "تنبيه",
      body: "هذا الاشتراك توضيحي ولا يُنشئ التزامات قانونية ملزمة على أي طرف، ولا يعد عقداً. تظل سياسات المنصة المنشورة هي المرجع.",
    },
  },
} as const;

export const IndividualAcknowledgement = ({ lang, data, documentDate }: IndividualAcknowledgementProps) => {
  const today = documentDate ?? new Date();
  const dateStr = today.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-GB');
  const isRTL = lang === 'ar';
  const t = COPY[lang];
  const dirAttr: 'rtl' | 'ltr' = isRTL ? 'rtl' : 'ltr';
  const alignClass = isRTL ? 'text-right' : 'text-left';
  const listIndentClass = isRTL ? 'pr-5' : 'pl-5';

  return (
    <div dir={dirAttr} className={`space-y-3 text-sm leading-6 text-foreground ${alignClass}`}>
      <div className="text-center space-y-0.5">
        <h2 className="text-lg font-bold">{t.title}</h2>
        <p className="text-muted-foreground">{data.city || "—"} — {dateStr}</p>
        <p className="text-xs text-muted-foreground italic">{t.disclaimer}</p>
      </div>

      <div className={`rounded-lg border border-border p-2 bg-muted/30 text-sm ${alignClass}`}>
        <p>{t.labels.name}: <span className="font-medium">{data.applicantName || "—"}</span></p>
        <p>{t.labels.email}: <span className="font-medium">{data.email}</span></p>
        <p>{t.labels.nationalId}: <span className="font-medium">{data.nationalIdNumber || "—"}</span></p>
        <p>{t.labels.city}: <span className="font-medium">{data.city || "—"}</span></p>
        <p>{t.labels.date}: <span className="font-medium">{dateStr}</span></p>
      </div>

      <Section title={t.welcome.title} dir={dirAttr}>
        {t.welcome.body}
      </Section>

      <Section title={t.youGet.title} dir={dirAttr}>
        <ul className={`list-disc ${listIndentClass} space-y-0.5 ${alignClass}`}>
          {t.youGet.items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.expected.title} dir={dirAttr}>
        <ul className={`list-disc ${listIndentClass} space-y-0.5 ${alignClass}`}>
          {t.expected.items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.commissions.title} dir={dirAttr}>
        {t.commissions.body}
      </Section>

      <Section title={t.ending.title} dir={dirAttr}>
        {t.ending.body}
      </Section>

      <div className={`rounded-lg bg-amber-500/10 border border-amber-500/20 p-2 text-xs text-foreground ${alignClass}`}>
        <strong>{t.notice.label}:</strong> {t.notice.body}
      </div>
    </div>
  );
};

const Section = ({ title, children, dir }: { title: string; children: React.ReactNode; dir?: 'ltr' | 'rtl' }) => (
  <div dir={dir} className={dir === 'rtl' ? 'text-right' : 'text-left'}>
    <p className="font-semibold mb-0.5">{title}</p>
    <div className="text-muted-foreground">{children}</div>
  </div>
);

export default IndividualAcknowledgement;
